import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import type { SingleResponse } from '@/types/api'
import type { ImportPreview, ImportResult } from '@/components/EmployeeImportModal'
import { pollEmployeeImport } from '@/services/modules/employeeImportPolling'

export type CompanyContactType = 'EMAIL' | 'TELEFONE' | 'CELULAR' | 'WHATSAPP'

export interface CompanyContact {
  id?: string
  type: CompanyContactType
  label: string | null
  value: string
  sortOrder?: number
}

export interface CompanyPlanSelected {
  id: string
  name: string
  planTypeRelationshipId: string | null
  pivotStatus: string
}

export interface CompanyAddress {
  postalCode: string | null
  address:    string | null
  number:     string | null
  complement: string | null
  district:   string | null
  city?: {
    id: string
    city: string
    stateId?: string
    state?: { id: string; state: string; abbreviation: string } | null
  } | null
}

export interface Company {
  id: string
  name: string
  corporateName: string | null
  cpfCnpj: string
  type: 'PESSOA FÍSICA' | 'PESSOA JURÍDICA'
  email: string | null
  phone: string | null
  mobile: string | null
  dueDay: number
  paymentMethod: 'BOLETO' | 'PIX' | 'CARTÃO DE CRÉDITO' | 'CARTÃO DE DÉBITO' | null
  status: 'ATIVO' | 'INATIVO'
  observations: string | null
  username: string | null
  titularesCount?: number | null
  address?: CompanyAddress
  manager?: { id: string; name: string; type?: string } | null
  contacts?: CompanyContact[]
  plans?: CompanyPlanSelected[]
  formatted?: {
    cpfCnpj?: string
    phone?: string
    mobile?: string
    postalCode?: string
    createdAt?: string
    updatedAt?: string
  }
}

export interface CompanyDocument {
  id: string
  name: string
  mime: string | null
  size: number | null
  url: string | null
  createdAt: string | null
}

export interface CompanyEmployeeRow {
  branch: string | null
  type: 'TITULAR' | 'DEPENDENTE'
  name: string
  cpf: string | null
  /** Carteirinha agrupada em 4 blocos (formatada no backend). */
  card: string | null
  /** dd/mm/aaaa (formatado no backend). */
  birthDate: string | null
  plan: string | null
  holderName: string | null
  status: string
}

/** Grupo de usuários elegíveis a gestor, agrupados por tipo no select. */
export interface ManagerGroup {
  type: 'DIRETOR' | 'SUPERVISOR' | 'AFILIADO'
  users: Array<{ id: string; name: string }>
}

const base = createCrudService<Company>('/commercial/companies')

export const CompaniesService = {
  ...base,

  /** Informações de comissionamento (responsável direto + árvore). */
  async commissionInfo(companyId: string): Promise<import('@/components/commercial/CommissionInfoPanel').CommissionInfo> {
    const r = await api.get<{ data: import('@/components/commercial/CommissionInfoPanel').CommissionInfo }>(`/commercial/companies/${companyId}/commission-info`)
    return r.data.data
  },

  // -- Documentos --
  async documents(companyId: string): Promise<CompanyDocument[]> {
    const r = await api.get<{ data: CompanyDocument[] }>(`/commercial/companies/${companyId}/documents`)
    return r.data.data
  },
  async uploadDocument(companyId: string, file: File, name?: string): Promise<CompanyDocument> {
    const fd = new FormData()
    fd.append('file', file)
    if (name) fd.append('name', name)
    const r = await api.post<{ data: CompanyDocument }>(`/commercial/companies/${companyId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data.data
  },
  async deleteDocument(companyId: string, docId: string): Promise<void> {
    await api.delete(`/commercial/companies/${companyId}/documents/${docId}`)
  },

  // -- Exportar funcionários --
  async employeesExportData(companyId: string, include: 'holders' | 'holders_dependents'): Promise<{ data: CompanyEmployeeRow[]; hasBranches: boolean }> {
    const r = await api.get<{ data: CompanyEmployeeRow[]; meta: { hasBranches: boolean } }>(`/commercial/companies/${companyId}/employees-export-data`, { params: { include } })
    return { data: r.data.data, hasBranches: r.data.meta.hasBranches }
  },

  // -- Gerar faturamento --
  async generateBilling(companyId: string, month: number, year: number): Promise<{ collaborators: number; total: number }> {
    const r = await api.post<{ data: { collaborators: number; total: number } }>(`/commercial/companies/${companyId}/generate-billing`, { month, year })
    return r.data.data
  },

  /**
   * Verifica se um `username` está disponível.
   * Em edição, passe `exceptId` (id da company atual) para ignorá-la.
   */
  async checkUsername(username: string, exceptId?: string): Promise<boolean> {
    const r = await api.get<SingleResponse<{ available: boolean }>>('/commercial/companies/check-username', {
      params: { username, except_id: exceptId },
    })
    return r.data.data.available
  },

  /**
   * Verifica se um CPF/CNPJ já está cadastrado em outra empresa.
   * Em edição, passe `exceptId` para ignorar a empresa atual.
   */
  async checkCpfCnpj(cpfCnpj: string, exceptId?: string): Promise<boolean> {
    const r = await api.get<SingleResponse<{ available: boolean }>>('/commercial/companies/check-cpf-cnpj', {
      params: { cpf_cnpj: cpfCnpj, except_id: exceptId },
    })
    return r.data.data.available
  },

  /** Usuários elegíveis a Gestor (Diretor, Supervisor, Afiliado) agrupados. */
  async managerOptions(): Promise<ManagerGroup[]> {
    const r = await api.get<{ data: ManagerGroup[] }>('/commercial/companies/manager-options')
    return r.data.data
  },

  /**
   * Gera o acesso como o usuário EMPRESA (impersonação). Retorna o usuário
   * serializado (mesmo shape do sign-in) + accessToken.
   */
  async impersonate(id: string): Promise<CompanyImpersonation> {
    const r = await api.get<{ data: CompanyImpersonation }>(`/commercial/companies/${id}/impersonate`)
    return r.data.data
  },

  // ----- Importação de funcionários (admin) -----
  async employeesImportTemplate(id: string): Promise<Blob> {
    const r = await api.get(`/commercial/companies/${id}/employees-import-template`, { responseType: 'blob' })
    return r.data as Blob
  },
  async employeesImportPreview(id: string, file: File): Promise<ImportPreview> {
    const fd = new FormData(); fd.append('file', file)
    const r = await api.post<{ data: ImportPreview }>(`/commercial/companies/${id}/employees-import-preview`, fd)
    return r.data.data
  },
  /** Prévia do impacto — quantos colaboradores e dependentes mudariam. */
  async statusPreview(id: string, status: 'ATIVO' | 'INATIVO'): Promise<CompanyStatusCascade> {
    const r = await api.get<{ data: CompanyStatusCascade }>(
      `/commercial/companies/${id}/status-preview`, { params: { status } },
    )
    return r.data.data
  },

  /** Muda o status e devolve quantos foram junto na cascata. */
  async setStatusCascading(id: string, status: 'ATIVO' | 'INATIVO'): Promise<CompanyStatusCascade> {
    const r = await api.put<{ data: unknown; meta?: { cascade?: CompanyStatusCascade } }>(
      `/commercial/companies/${id}/status`, { status },
    )
    return r.data.meta?.cascade ?? { holders: 0, dependents: 0 }
  },

  async employeesImportConfirm(id: string, file: File): Promise<ImportResult> {
    // Assíncrono: dispara o job e acompanha o status (não segura a request 30s).
    const fd = new FormData(); fd.append('file', file)
    const r = await api.post<{ data: { id: string } }>(`/commercial/companies/${id}/employees-import-confirm`, fd)
    return pollEmployeeImport(`/commercial/companies/${id}/employees-import-status/${r.data.data.id}`)
  },
}

/** Quantas pessoas a mudança de status da empresa afeta. */
export interface CompanyStatusCascade {
  holders: number
  dependents: number
}

/** Usuário EMPRESA serializado + token (resposta do impersonate). */
export interface CompanyImpersonation {
  id: string
  name: string
  username: string
  email: string | null
  type: string
  status: string
  slug: string | null
  companyId: string | null
  accessToken: string
  hierarchy?: { id: string; name: string; authorities: string[] } | null
  [key: string]: unknown
}
